<template>
  <section
    :class="[{ 'owc-banner-teaser__theme-dark': isDarkMode }, 'owc-banner-teaser']"
    :data-theme="brand"
  >
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <wb-banner-teaser :theme="isDarkMode ? 'dark' : ''">
      <div class="owc-banner-teaser__image-container">
        <div v-if="tags && tags.length > 0" class="wb-banner-teaser__tag-wrapper">
          <wb-tag
            v-for="(tag, index) in tags"
            :key="index"
            class="owc-banner-teaser__tag-item"
            :variant="getTagClassByType(tag.type)"
          >
            {{ tag.title }}
          </wb-tag>
        </div>
        <GenericImageComponent v-if="image" :imageData="image" />
      </div>
      <wb-banner-teaser-content>
        <h2
          v-if="headline"
          v-html="headline"
          class="wb-banner-teaser-content__heading wb-type-heading-m"
        ></h2>
        <div v-if="copy" v-html="copy" class="wb-banner-teaser-content__description wb-type-copy"></div>
        <div class="wb-banner-teaser-content__bottom">
          <a
            v-if="ctaSecondary"
            :class="[
              'wb-button wb-button--secondary wb-button--large',
              { 'wb-button--theme-dark': isDarkMode },
            ]"
            :aria-label="ctaSecondary.aria"
            :target="ctaSecondary.target"
            :href="ctaSecondary.url"
          >
            {{ ctaSecondary.label }}
          </a>
          <a
            v-if="ctaPrimary"
            :aria-label="ctaPrimary.aria"
            :target="ctaPrimary.target"
            :href="ctaPrimary.url"
            class="wb-button wb-button--primary wb-button--large"
          >
            {{ ctaPrimary.label }}
          </a>
        </div>
      </wb-banner-teaser-content>
    </wb-banner-teaser>
    <div v-if="footnotes" class="wb-grid-container owc-banner-teaser__footnotes">
      <globalelem-footnote-component
        :module-id="moduleId"
        :theme="isDarkMode ? 'dark' : 'light'"
      ></globalelem-footnote-component>
    </div>
  </section>
</template>

<script lang="ts">
/**
 * `BannerTeaser` - Banner Teaser Component.
 * For now this is just a owc wrapper component - This component uses workbench wb-banner-teaser.
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { BannerTeaserComponent, BannerTeaserLink } from './BannerTeaser';
import { CTAButton, TagType } from './BannerTeaser.enums';
import { IMAGE_RATIO_DEFAULT } from '../src/BannerTeaser.config';
import GenericImageGenerator from '@owc-utils/GenericImage/GenericImageGenerator';
import GenericImageComponent from '@owc-packages/GenericImage/src/GenericImage.vue';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import Personalisation from '@owc-utils/Mixins/Personalisation';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { getLocalFootnotes, setModuleId } from '@owc-utils/Footnotes/FootnotesHelper';
import { setBrandTheme } from '@owc-utils/Theming/theming';

export default (ComponentInitializeDispatcher as BannerTeaserComponent).extend({
  name: 'owc-banner-teaser',
  components: {
    GenericImageComponent,
    ValidationBoxWrapper,
  },
  mixins: [OwcDataComponent, Personalisation],
  computed: {
    image: function() {
      const image = this.personaliseableData?.image;

      if (!image) {
        return null;
      }

      return new GenericImageGenerator(image, [], IMAGE_RATIO_DEFAULT);
    },
    headline: function() {
      return this.personaliseableData?.headline || '';
    },
    copy: function() {
      return this.personaliseableData?.copy || '';
    },
    isDarkMode: function() {
      return Boolean(this.personaliseableData?.darkMode);
    },
    ctaBtns: function() {
      return this.personaliseableData?.links || [];
    },
    ctaPrimary: function() {
      return this.ctaBtns.find((ctaBtn: BannerTeaserLink) => ctaBtn.type === CTAButton.primary);
    },
    ctaSecondary: function() {
      return this.ctaBtns.find((ctaBtn: BannerTeaserLink) => ctaBtn.type === CTAButton.secondary);
    },
    tags: function() {
      return this.personaliseableData?.tags || [];
    },
    footnotes() {
      return getLocalFootnotes(this.personaliseableData);
    },
    moduleId() {
      return setModuleId(this);
    },
    brand() {
      return setBrandTheme();
    },
  },
  methods: {
    getTagClassByType(tagType: number) {
      return TagType[tagType] || '';
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/rtl-utils';
@import '@owc-styles/utils/rte';
@import '@owc-styles/utils/theming';

$bannerTeaserModuleName: 'owc-banner-teaser';
$max-tag-size: 70px;

.#{$bannerTeaserModuleName} {
  &__theme-dark {
    background: var(--wb-black);
  }
  &__image-container {
    position: relative;

    grid-column: 1 / span 4;

    @include breakpoint-to(mq4) {
      grid-column: 1 / span 5;
    }
    @include breakpoint-to(mq2) {
      grid-column: none;
    }

    .wb-banner-teaser__tag-wrapper {
      position: absolute;

      top: var(--wb-spacing-s);
    }
  }
  &__tag-item {
    max-width: $max-tag-size;
    overflow: hidden;
  }
  wb-banner-teaser {
    padding-top: var(--wb-spacing-s);
    padding-bottom: var(--wb-spacing-s);
  }
  .wb-banner-teaser-content__bottom a {
    @include breakpoint-to(mq2) {
      width: 100%;
    }

    &:first-child {
      margin-right: var(--wb-spacing-3xs);
      margin-bottom: var(--wb-spacing-xxs);
    }
  }
  &__footnotes {
    padding-bottom: var(--wb-spacing-s);
  }
  &--dark {
    background-color: var(--wb-black);
  }
  .wb-banner-teaser-content__description {
    @include wb-type-copy;
  }
}

@include rtl-rule('.wb-banner-teaser-content__bottom a:first-child', false) {
  margin-right: 0;
  margin-left: var(--wb-spacing-3xs);
}
</style>
